import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Collapse,
} from '@mui/material';
import { ChevronDown, Pencil, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const POList = () => {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [poToDelete, setPoToDelete] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
      return;
    }

    axios.get("https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/auth/profile", {
      headers: { "Authorization": `Bearer ${token}` },
      withCredentials: true,
    })
      .catch(error => {
        console.error("Authentication failed:", error.response?.data);
        navigate("/login");
      });

    const fetchPos = async () => {
      setLoading(true);
      try {
        const response = await axios.get('https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/pos');
        setPos(response.data);
      } catch (error) {
        console.error('Error fetching POs:', error);
        toast.error('Failed to load purchase orders');
      } finally {
        setLoading(false);
      }
    };

    fetchPos();
  }, [navigate]);

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleEdit = (id) => navigate(`/po-edit/${id}`);

  const handleDeleteClick = (id) => {
    setPoToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!poToDelete || poToDelete.length !== 24) {
      toast.error('Invalid PO ID');
      return;
    }
    const t = toast.loading('Deleting purchase order…');
    try {
      await axios.delete(`https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/pos/${poToDelete}`);
      setPos((prevPos) => prevPos.filter((po) => po._id !== poToDelete));
      setDeleteModalOpen(false);
      setPoToDelete(null);
      toast.success('Purchase order deleted', { id: t });
    } catch (error) {
      console.error('Error deleting PO:', error.response ? error.response.data : error.message);
      toast.error(error.response?.data?.message || 'Error deleting PO', { id: t });
    }
  };

  return (
    <Box className="fade-in">
      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 3 }}>
        Purchase Lists
      </Typography>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 2fr 1.5fr',
          px: 2.5, py: 1.5,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'action.hover',
        }}>
          {['Name', 'Platform', 'Purchase Order', 'Actions'].map(h => (
            <Typography key={h} variant="caption" sx={{
              fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
              fontSize: '0.7rem', color: 'text.secondary',
            }}>
              {h}
            </Typography>
          ))}
        </Box>

        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Box
              key={`sk-${i}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 2fr 1.5fr',
                px: 2.5, py: 1.75,
                borderBottom: '1px solid', borderColor: 'divider',
                alignItems: 'center',
              }}
            >
              <Skeleton variant="text" width="65%" />
              <Skeleton variant="text" width="55%" />
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="rectangular" width={120} height={26} sx={{ borderRadius: 1 }} />
            </Box>
          ))
        ) : pos.length > 0 ? (
          pos.map((po) => (
            <React.Fragment key={po._id}>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 2fr 1.5fr',
                px: 2.5, py: 1.5,
                borderBottom: '1px solid', borderColor: 'divider',
                alignItems: 'center',
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'background-color 0.15s',
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{po.poName}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{po.platform}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {po.masterPoId ? po.masterPoId.poName : 'No Purchase Order assigned'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button
                    size="small"
                    onClick={() => toggleExpand(po._id)}
                    sx={{ minWidth: 32, p: 0.5 }}
                  >
                    <ChevronDown
                      size={16}
                      style={{
                        transform: expandedRows[po._id] ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </Button>
                  {((po.masterPoId && po.masterPoId.status === 'Open') || po.masterPoId === null) && (
                    <Button
                      size="small"
                      startIcon={<Pencil size={14} />}
                      onClick={() => handleEdit(po._id)}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Edit
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="error"
                    startIcon={<Trash2 size={14} />}
                    onClick={() => handleDeleteClick(po._id)}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>

              {/* Expandable Row */}
              <Collapse in={expandedRows[po._id]} timeout="auto" unmountOnExit>
                <Box sx={{
                  px: 3, py: 2,
                  borderBottom: '1px solid', borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    <strong>PO ID:</strong> {po._id}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    <strong>Created At:</strong> {new Date(po.createdAt).toLocaleDateString()}
                  </Typography>
                  {po.masterPoId && (
                    <Box sx={{
                      mt: 1.5, p: 1.5,
                      border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                    }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        <strong>Master PO Name:</strong> {po.masterPoId.poName}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        <strong>Master PO Status:</strong> {po.masterPoId.status}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        <strong>Master PO Created:</strong> {po.masterPoId.createdAt ? new Date(po.masterPoId.createdAt).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </React.Fragment>
          ))
        ) : (
          <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No Purchase Lists available
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Master Purchase Order?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default POList;
